package br.com.duoma.duomaproducao.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.rememberScrollbarAdapter
import androidx.compose.foundation.VerticalScrollbar
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import br.com.duoma.duomaproducao.data.groupAndAggregate
import br.com.duoma.duomaproducao.data.mapIndexed
import br.com.duoma.duomaproducao.model.WorkCenter
import br.com.duoma.duomaproducao.ui.Screen
import br.com.duoma.duomaproducao.ui.components.Filter
import br.com.duoma.duomaproducao.ui.components.HeatmapCell
import br.com.duoma.duomaproducao.ui.components.HeatmapChart
import br.com.duoma.duomaproducao.ui.components.ProductionFlowchart
import br.com.duoma.duomaproducao.ui.components.SectionHeader
import br.com.duoma.duomaproducao.ui.components.WorkCenterBarChart
import br.com.duoma.duomaproducao.ui.components.WorkCenterLineChart
import br.com.duoma.duomaproducao.ui.misc.mediaQuery
import br.com.duoma.duomaproducao.viewmodel.ExecutionInfo
import br.com.duoma.duomaproducao.viewmodel.LineChartSeries
import br.com.duoma.duomaproducao.viewmodel.rememberLineChartManager
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianChartModelProducer
import com.patrykandpatrick.vico.compose.cartesian.data.CartesianLayerRangeProvider
import com.patrykandpatrick.vico.compose.cartesian.data.columnSeries
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Month
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.yearMonth
import kotlinx.serialization.Serializable
import kotlin.collections.mapValues

@Serializable
data object DashboardScreen : Screen {
    override val route = "dashboard"
}

/** Returns the Monday of the ISO week containing this date. */
private fun LocalDate.weekMonday(): LocalDate = this.minus(DatePeriod(days = dayOfWeek.isoDayNumber - 1))
val translations = mapOf<Month, String>(
    Month.JANUARY to "Janeiro",
    Month.FEBRUARY to "Fevereiro",
    Month.MARCH to "Março",
    Month.APRIL to "Abril",
    Month.MAY to "Maio",
    Month.JUNE to "Junho",
    Month.JULY to "Julho",
    Month.AUGUST to "Agosto",
    Month.SEPTEMBER to "Setembro",
    Month.OCTOBER to "Outubro",
    Month.NOVEMBER to "Novembro",
    Month.DECEMBER to "Dezembro",
)
fun Month.fullName() : String = translations[this]!!
val WC_COLORS = listOf(
    Color(0xff21f3aa), // CORTE          – blue
    Color(0xFF4CAF50), // LAMINACAO      – green
    Color(0xFF9C27B0), // MARCENARIA     – purple
    Color(0xFFFF9800), // MONTAGEM       – amber
    Color(0xFFE91E63), // PREPARACAO_EST – pink
    Color(0xFF00BCD4), // COSTURA        – cyan
    Color(0xFFFF5722), // ESPUMACAO      – deep orange
    Color(0xFF795548), // TAPECARIA      – brown
)
@Composable
fun DashboardScreen(executionInfo: ExecutionInfo, onWorkcenterClick: (WorkCenter) -> Unit) {
    val scrollState = rememberScrollState()

    val monthlyProductionPerWorkcenterManager =
        rememberLineChartManager(
            executionInfo,
            {
                groupAndAggregate(it, { it.date.yearMonth }, { it.quantity.toDouble() }) {
                    it.date.yearMonth >= LocalDate.now().minus(DatePeriod(months = 6)).yearMonth
                }.entries.sortedBy { it.key }.mapIndexed { i, (k, v) ->
                    LineChartSeries(v, k.month.fullName(), WC_COLORS[i])
                }
            },
            WorkCenter.entries.toList(),
            { it._name },
            { wc, map -> map.toMutableMap().apply { this[wc] = !this[wc]!! } },
            true
        )
    val monthRange = remember { (12 downTo 0)}
    val months = remember { monthRange.map { LocalDate.now().minus(DatePeriod(months = it)).yearMonth }}
    val monthlyProductionManager = rememberLineChartManager(
        executionInfo,
        {
            it.mapValues { (_, v) ->
                v.groupBy { it.date.yearMonth }
                    .filterKeys { it in months }
                    .entries.sortedBy { it.key }
                    .associate { it.key to it.value.sumOf { it.quantity }.toDouble() }
            }.mapIndexed { i, k, v -> LineChartSeries(v, k._name, WC_COLORS[i]) }
        },
        months,
        { it.month.fullName() },
        null,
        false
    )
    val fmt = remember { LocalDate.Format { day(); char('/'); monthNumber(); char('/'); year() } }
    // ── Bar chart (last month totals) ─────────────────────────────────
    val lastMonth = remember { LocalDate.now().minus(DatePeriod(months = 1)) }
    val barModel = remember { CartesianChartModelProducer() }
    LaunchedEffect(executionInfo) {
        barModel.runTransaction {
            columnSeries {
                series(
                    WorkCenter.entries.map { wc ->
                        executionInfo[wc]?.sumOf {
                            if (it.date > lastMonth) it.quantity else 0
                        } ?: 0
                    }
                )
            }
        }
    }
    // ── Bar chart (monthly totals) ─────────────────────────────────
    val monthlyTotals = remember { CartesianChartModelProducer() }
    var selectedWorkcenter by remember { mutableStateOf(WorkCenter.entries.first())}
    var series by remember(executionInfo) {
        mutableStateOf(
            executionInfo.mapValues { (_, v) ->
                v.groupBy { it.date.yearMonth }
                    .filterKeys { it in months }
                    .entries.sortedBy { it.key }
                    .map { it.value.sumOf { it.quantity } }
            }
        )
    }
    val maxValue by remember(series) {derivedStateOf {
        if (series.isEmpty()) 0.0 else series.maxOf { it.value.max() }
        }
    }
    LaunchedEffect(series, selectedWorkcenter) {
        monthlyTotals.runTransaction {
            if (executionInfo[selectedWorkcenter] == null) return@runTransaction
            columnSeries {
                series(
                    if (executionInfo[selectedWorkcenter] == null) months.map { 0 }
                    else series[selectedWorkcenter]!!
                )
            }
        }
    }
    val heatMapData = remember(executionInfo) {
        executionInfo.flatMap { (wc, execs) ->
            execs.groupBy { it.date.yearMonth }
                .filterKeys { it in months }
                .entries.sortedBy { it.key }
                .map { (ym, items) ->
                    HeatmapCell(wc, ym, items.sumOf { it.quantity }.toFloat())
                }
        }
    }


    Box(Modifier.fillMaxSize()) {
        Column(
            Modifier.fillMaxSize().verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally

        ) {
            Column(Modifier.widthIn(max = 1350.dp).fillMaxWidth().padding(horizontal = mediaQuery(
                32.dp,
                12.dp
            )
            )) {
                Spacer(Modifier.height(28.dp))

                // ── Production flowchart ──────────────────────────────────
                SectionHeader(
                    "Fluxo de Produção - Média de ${lastMonth.format(fmt)} à ${LocalDate.now().minus(DatePeriod(days = 1)).format(fmt)}"
                )
                Spacer(Modifier.height(20.dp))
                ProductionFlowchart(
                    executionInfo,
                    onClick = onWorkcenterClick
                )

                // ── Weekly line chart ─────────────────────────────────────
                Spacer(Modifier.height(40.dp))
                SectionHeader("Produção Mensal por Centro de Trabalho")
                Spacer(Modifier.height(16.dp))
                WorkCenterLineChart(
                    monthlyProductionPerWorkcenterManager,
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(Modifier.height(40.dp))
                SectionHeader("Mapa de Calor de Produção")
                Spacer(Modifier.height(16.dp))
                HeatmapChart(
                    heatMapData,
                    Modifier.fillMaxWidth(),
                    colLabels = remember(months) { months.map { it.month.fullName() to it } },
                    rowLabels = remember { WorkCenter.entries.map { it._name to it } })

                Spacer(Modifier.height(40.dp))
                SectionHeader("Produção Mensal")
                Spacer(Modifier.height(16.dp))
                WorkCenterLineChart(
                    monthlyProductionManager,
                    modifier = Modifier.fillMaxWidth()
                )

                // ── Monthly bar chart ─────────────────────────────────────
                Spacer(Modifier.height(40.dp))
                SectionHeader(
                    "Evolução de Produção por Centro de Trabalho"
                )
                Spacer(Modifier.height(16.dp))
                WorkCenterBarChart(
                    monthlyTotals,
                    Modifier.fillMaxWidth().height(340.dp),
                    months.map { it.month.fullName() },
                    filter = {
                        Filter(
                                                WorkCenter.entries.map { it to (it == selectedWorkcenter)},
                                                null,
                                                { selectedWorkcenter = it
                                                    println(selectedWorkcenter)
                                                },
                                            )
                    },
                    rangeProvider = CartesianLayerRangeProvider.fixed(minY = 0.0, maxY = maxValue.toDouble())
                )
                // ── Monthly bar chart ─────────────────────────────────────
                Spacer(Modifier.height(40.dp))
                SectionHeader(
                    "Produção por Centro de Trabalho — ${lastMonth.format(fmt)} a ${LocalDate.now().format(fmt)}"
                )
                Spacer(Modifier.height(16.dp))
                WorkCenterBarChart(
                    barModel,
                    Modifier.fillMaxWidth().height(340.dp)
                )
            }



            Spacer(Modifier.height(80.dp))
        }
        VerticalScrollbar(
            adapter = rememberScrollbarAdapter(scrollState),
            modifier = Modifier
                .align(Alignment.CenterEnd)
                .fillMaxHeight()
                .padding(end = 4.dp)
        )
    }
}
